<template>
  <div class="image-file-upload">
    <div
      class="image-file-upload-container"
      :class="{ 'image-file-upload-container--drag-active': fileDragActive }"
      v-if="images && images.length > 0"
      @drop.prevent="dropHandler"
      @dragenter.prevent="disabled ? '' : dragEnter()"
      @dragover.prevent="disabled ? '' : dragEnter()"
      @dragleave.prevent="disabled ? '' : dragLeave()"
      @change="disabled ? '' : handleFileUpload()"
    >
      <div
        v-for="(image, idx) in images"
        :key="`image-${idx}`"
        class="image-wrapper"
        @mouseenter="hoveredImageIndex = idx"
        @mouseleave="hoveredImageIndex = null"
      >
        <img :src="image.url" :alt="image.value" class="fixed-image" @click="viewImage(image.url)" />
        <BaseRoundButton
          v-if="hoveredImageIndex === idx"
          class="image-view-btn"
          :size="60"
          icon="zoom-in"
          :tooltip-text="$t('actions.view')"
          tooltipPlacement="top"
          isNeutral
          isTransparent
          @click.native="viewImage(image.url)"
        />
        <BaseRoundButton
          v-if="hoveredImageIndex === idx"
          class="image-delete-btn"
          :size="30"
          icon="trash-2"
          :tooltip-text="$t('actions.delete')"
          tooltipPlacement="top"
          isRed
          isTransparent
          @click.native="onDeleteClicked(image)"
        />
      </div>
    </div>
    <FileUpload
      v-else
      display-horizontal
      multiple
      icon="file-plus"
      :title="$t('evaluation-grid.add-field.add-image')"
      :description="$t('evaluation-grid.add-field.add-image-description')"
      :allowedFiles="'.png,.jpeg,.jpg'"
      :isLoading="isUploading"
      :disabled="isUploading"
      @add-files="uploadImages"
    />
    <div v-if="viewingImage" class="image-viewer-overlay" @click="closeImageViewer">
      <div class="image-viewer-content" @click.stop>
        <img :src="viewingImage" alt="Full size image" />
        <BaseRoundButton
          class="image-viewer-close"
          :size="40"
          icon="x"
          :tooltip-text="$t('actions.close')"
          tooltipPlacement="left"
          @click.native="closeImageViewer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FileUpload from './FileUpload.vue';

export default {
  name: 'ImageFileUpload',
  components: {
    BaseRoundButton,
    FileUpload,
  },
  props: {
    images: {
      type: Array,
    },
    isUploading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoveredImageIndex: null,
      viewingImage: null,
      hovered: false,
      fileDragActive: false,
    };
  },
  methods: {
    onDeleteClicked(image) {
      this.$emit('on-delete', image);
    },
    viewImage(imageUrl) {
      this.viewingImage = imageUrl;
    },
    closeImageViewer() {
      this.viewingImage = null;
    },
    dropHandler(e) {
      if (this.disabled) return;
      this.fileDropped(e);
    },
    fileDropped(event) {
      const { files } = event.dataTransfer;
      this.fileDragActive = false;
      this.uploadImages(files);
    },
    uploadImages(files) {
      this.$emit('add-files', files);
    },
    dragEnter() {
      this.fileDragActive = true;
    },
    dragLeave() {
      this.fileDragActive = false;
    },
    handlePaste(event) {
      if (this.disabled || !this.isHovered) return;

      const items = event.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        this.$emit('add-files', files);
      }
    },
  },
};
</script>

<style lang="scss">
.image-file-upload {
  width: 100%;

  .image-file-upload-container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border: 1px dashed transparent;
    border-radius: 3px;
    transition: all 200ms ease;
    width: 100%;
    gap: 8px;
    padding: 10px;
    margin-bottom: 8px;
    justify-content: start;
    flex-wrap: wrap;

    &--drag-active {
      border-color: $color-neutral;
      background: $color-neutral-pale;
    }

    .fixed-image {
      display: block;
      width: 200px;
      height: 120px;
      object-fit: cover;
      border-radius: $border-radius-small;
      cursor: pointer;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.9;
      }
    }

    .image-wrapper {
      position: relative;
      display: inline-block;

      // grey overlay on the image
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
        border-radius: $border-radius-small;
        opacity: 0;
        transition: opacity 0.5s ease;
        pointer-events: none;
        z-index: 1;
      }

      &:hover::before {
        opacity: 1;
      }

      .image-view-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 2;
      }

      .image-delete-btn {
        position: absolute;
        top: 4px;
        right: 4px;
        z-index: 2;
      }
    }
  }
}

.image-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
}

.image-viewer-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
  background: $color-white;
  padding: 8px;
  border-radius: $border-radius-small;

  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: $border-radius-small;
  }

  .image-viewer-close {
    position: absolute;
    top: -16px;
    right: -16px;
  }
}
</style>
